/**
 * PHI-gated admin routes (GDN-2772) answer 403 with a machine-readable
 * `phi_access_denied` code rather than a bare Forbidden: Admin and
 * `staff:phi_read` get through, a plain Moderator does not. Callers need to
 * tell that apart from a generic failure so the UI can say what's actually
 * wrong instead of "something went wrong".
 *
 * The code can sit at either of two depths. `auth0BaseQuery` re-wraps every
 * failure as `{ status, data: { status, message, data: <original body> } }`,
 * so in the running app the backend's body is nested one level in; tests and
 * mocks that stub the query hook hand back the raw body. Both are real shapes
 * this has to recognise — checking only one is how a gate message silently
 * degrades into a generic error toast.
 */

/** The shape the backend's ClientErrorResponse serialises to. */
interface PhiErrorBody {
  error_code?: string;
  message?: string;
  data?: unknown;
}

interface MaybeQueryError {
  status?: number | string;
  data?: unknown;
}

const asBody = (value: unknown): PhiErrorBody | undefined =>
  value && typeof value === 'object' ? (value as PhiErrorBody) : undefined;

/** The error body, and the body it wraps — the code may be in either. */
const candidateBodies = (error: unknown): PhiErrorBody[] => {
  const outer = asBody((error as MaybeQueryError | undefined)?.data);
  if (!outer) return [];
  const inner = asBody(outer.data);
  return inner ? [outer, inner] : [outer];
};

/**
 * True when an RTK Query error is the PHI gate refusing the caller, as opposed
 * to any other 403 or a transport failure.
 */
export const isPhiAccessDenied = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  if ((error as MaybeQueryError).status !== 403) return false;
  return candidateBodies(error).some(body => body.error_code === 'phi_access_denied');
};

/** The gate's own explanation, when it sent one worth showing. */
export const phiAccessDeniedMessage = (error: unknown): string | undefined => {
  if (!isPhiAccessDenied(error)) return undefined;
  // Prefer the body that actually carries the code — the wrapper's `message`
  // is a generic fallback the base query synthesises.
  const withCode = candidateBodies(error).find(
    body => body.error_code === 'phi_access_denied',
  );
  return withCode?.message || undefined;
};
